import { NotificationProps } from '@progress/kendo-react-notification';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FpNotificationData } from 'components/molecules/FpNotification';
import {
  getAPIErrorNotification, getErrorNotification, getSuccessNotification, getWarningNotification,
} from 'components/molecules/FpNotification/generator';
import { Problem } from 'types/entity';
import { ulid } from 'ulid';

export const NOTIFICATION_STORE_NAME = 'notification';

export interface NotificationState {
  notifications: FpNotificationData[]
  toDownloadNotificationQueue: FpNotificationData[]
}

export const initialNotificationState: NotificationState = {
  notifications: [],
  toDownloadNotificationQueue: [],
};

const DOWNLOADING_LIMIT = 10;

const reachDownloadingLimit = (notifications: FpNotificationData[]): boolean => notifications.filter((n: FpNotificationData) => !!n.downloadStatus && n.downloadStatus.finished === false).length === DOWNLOADING_LIMIT;

export const notificationSlice = createSlice({
  name: NOTIFICATION_STORE_NAME,
  initialState: initialNotificationState,
  reducers: {
    addSuccess: (state, action: PayloadAction<{ messageId: string, values?: string[] }>) => {
      const { messageId, values } = action.payload;
      const newest: FpNotificationData = getSuccessNotification(messageId, values);
      state.notifications = [...state.notifications, newest];
    },
    addWarning: (state, action: PayloadAction<{ messageId: string, values?: string[] }>) => {
      const { messageId, values } = action.payload;
      const newest: FpNotificationData = getWarningNotification(messageId, values);
      state.notifications = [...state.notifications, newest];
    },
    addError: (state, action: PayloadAction<{ messageId: string, values?: string[] }>) => {
      const { messageId, values } = action.payload;
      const newest: FpNotificationData = getErrorNotification(messageId, values);
      state.notifications = [...state.notifications, newest];
    },
    addApiError: (state, action: PayloadAction<Problem | undefined>) => {
      const newest: FpNotificationData = getAPIErrorNotification(action.payload);
      state.notifications = [...state.notifications, newest];
    },
    addFileDownloadNotification: (state, action: PayloadAction<{ downloadUrl: string, fileName: string, values: string[] }>) => {
      const { downloadUrl, fileName, values } = action.payload;
      const id = ulid();
      const messageId = '';
      const type: NotificationProps['type'] = { style: 'info' };
      const downloadStatus = {
        url: downloadUrl,
        finished: false,
      };
      const newest: FpNotificationData = {
        id, messageId, type, downloadStatus, fileName, values,
      };

      if (!reachDownloadingLimit(state.notifications)) {
        state.toDownloadNotificationQueue = [...state.toDownloadNotificationQueue, newest];
        state.notifications = [...state.notifications, state.toDownloadNotificationQueue.shift() as FpNotificationData];
      } else {
        state.toDownloadNotificationQueue = [...state.toDownloadNotificationQueue, newest];
      }
    },
    setFileDownloadFinished: (state, action: PayloadAction<string>) => {
      const downloadNotification = state.notifications.find((notification) => notification.id === action.payload) as FpNotificationData;
      const newDownloadStatus = {
        url: downloadNotification.downloadStatus?.url as string,
        finished: true,
      };
      downloadNotification.downloadStatus = newDownloadStatus;

      if (!reachDownloadingLimit(state.notifications)) {
        const newDownload = state.toDownloadNotificationQueue.shift() as FpNotificationData;
        state.notifications = (newDownload ? [...state.notifications, newDownload] : state.notifications);
      }
    },
    removeOne: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
    },
  },
});

export const notificationActions = {
  ...notificationSlice.actions,
};

export default notificationSlice.reducer;
